/**
 * CaseEditor - Modal Component for Editing Standardized Cases
 * 
 * Provides a tabbed interface for editing case metadata, persona,
 * clinical data, vitals, and scoring rubric.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './CaseEditor.css';

const TABS = [
    { id: 'overview', label: 'Case Overview' },
    { id: 'metadata', label: 'Metadata' },
    { id: 'persona', label: 'Persona' },
    { id: 'clinical', label: 'Clinical Data' },
    { id: 'vitals', label: 'Vitals' },
    { id: 'rubric', label: 'Rubric' },
];

const CaseEditor = ({ caseId, onClose, onSave }) => {
    const { user, isAuthenticated, idToken, signIn, isConfigured } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [caseData, setCaseData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    // Load case data on mount
    useEffect(() => {
        if (caseId) {
            loadCase();
        }
    }, [caseId]);

    const loadCase = async () => {
        if (!idToken) {
            setError('Please sign in to view case data');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`/api/cases/${caseId}`, {
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to load case');
            }
            const data = await response.json();
            setCaseData(data.case);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!isAuthenticated) {
            setError('Please sign in to save changes');
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            const response = await fetch(`/api/cases/${caseId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({ case: caseData }),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to save case');
            }

            setHasChanges(false);
            if (onSave) onSave(caseData);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = (path, value) => {
        setCaseData(prev => {
            const newData = JSON.parse(JSON.stringify(prev)); // Deep clone
            const parts = path.split('.');
            let current = newData;
            for (let i = 0; i < parts.length - 1; i++) {
                if (current[parts[i]] === undefined) {
                    current[parts[i]] = {};
                }
                current = current[parts[i]];
            }
            current[parts[parts.length - 1]] = value;
            return newData;
        });
        setHasChanges(true);
    };

    const addArrayItem = (path, template) => {
        setCaseData(prev => {
            const newData = JSON.parse(JSON.stringify(prev));
            const parts = path.split('.');
            let current = newData;
            for (const part of parts) {
                current = current[part];
            }
            if (Array.isArray(current)) {
                // Generate proper sequential ID based on rubric type
                let newId = `NEW-${Date.now()}`;
                if (path.includes('critical_failures')) {
                    const existingCount = current.length;
                    newId = `CF-${existingCount + 1}`;
                } else if (path.includes('quality_failures')) {
                    const existingCount = current.length;
                    newId = `QF-${existingCount + 1}`;
                } else if (path.includes('excellence_signals')) {
                    const existingCount = current.length;
                    newId = `EX-${existingCount + 1}`;
                }
                current.push({ ...template, id: newId });
            }
            return newData;
        });
        setHasChanges(true);
    };

    const removeArrayItem = (path, index) => {
        setCaseData(prev => {
            const newData = JSON.parse(JSON.stringify(prev));
            const parts = path.split('.');
            let current = newData;
            for (const part of parts) {
                current = current[part];
            }
            if (Array.isArray(current)) {
                current.splice(index, 1);
            }
            return newData;
        });
        setHasChanges(true);
    };

    const updateArrayItem = (path, index, field, value) => {
        setCaseData(prev => {
            const newData = JSON.parse(JSON.stringify(prev));
            const parts = path.split('.');
            let current = newData;
            for (const part of parts) {
                current = current[part];
            }
            if (Array.isArray(current) && current[index]) {
                current[index][field] = value;
            }
            return newData;
        });
        setHasChanges(true);
    };

    // Render tab content
    const renderTabContent = () => {
        if (!caseData) return null;

        switch (activeTab) {
            case 'overview':
                // Group clinical data by category
                const groupedClinical = {};
                (caseData.clinical_data || []).forEach(item => {
                    const cat = item.category || 'Other';
                    if (!groupedClinical[cat]) groupedClinical[cat] = [];
                    groupedClinical[cat].push(item);
                });

                return (
                    <div className="tab-content overview-tab">
                        {/* Case ID */}
                        <div className="overview-section">
                            <h3>Case ID</h3>
                            <p className="overview-value">{caseData.case_id}</p>
                        </div>

                        {/* Metadata */}
                        <div className="overview-section">
                            <h3>Metadata</h3>
                            <div className="overview-grid">
                                <div><strong>Case Name:</strong> {caseData.metadata?.case_name}</div>
                                <div><strong>Domain:</strong> {caseData.metadata?.domain}</div>
                                <div><strong>Core Threat:</strong> {caseData.metadata?.core_threat}</div>
                                <div><strong>Cognitive Trap:</strong> {caseData.metadata?.cognitive_trap}</div>
                                <div><strong>Red Flag Density:</strong> {caseData.metadata?.red_flag_density}</div>
                                <div><strong>Difficulty:</strong> {caseData.metadata?.intended_difficulty}</div>
                            </div>
                        </div>

                        {/* Persona */}
                        <div className="overview-section">
                            <h3>Persona</h3>
                            <div className="overview-grid">
                                <div><strong>Age:</strong> {caseData.persona?.demographics?.age}</div>
                                <div><strong>Sex:</strong> {caseData.persona?.demographics?.sex}</div>
                                <div><strong>Affect:</strong> {caseData.persona?.psychometrics?.affect}</div>
                                <div><strong>Reliability:</strong> {caseData.persona?.psychometrics?.reliability}</div>
                                <div><strong>Medical Literacy:</strong> {caseData.persona?.psychometrics?.medical_literacy}</div>
                            </div>
                        </div>

                        {/* Clinical Data by Category */}
                        <div className="overview-section">
                            <h3>Clinical Data</h3>
                            {Object.keys(groupedClinical).map(category => (
                                <div key={category} className="clinical-category">
                                    <h4>{category}</h4>
                                    <ul>
                                        {groupedClinical[category].map((item, idx) => (
                                            <li key={idx}>
                                                <strong>{item.label}:</strong> {item.value}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        {/* Vitals */}
                        <div className="overview-section">
                            <h3>Vital Signs</h3>
                            <div className="overview-grid vitals-grid">
                                {caseData.vitals?.heart_rate && (
                                    <div><strong>HR:</strong> {caseData.vitals.heart_rate.value}</div>
                                )}
                                {caseData.vitals?.blood_pressure && (
                                    <div><strong>BP:</strong> {caseData.vitals.blood_pressure.value}</div>
                                )}
                                {caseData.vitals?.respiratory_rate && (
                                    <div><strong>RR:</strong> {caseData.vitals.respiratory_rate.value}</div>
                                )}
                                {caseData.vitals?.o2_saturation && (
                                    <div><strong>SpO2:</strong> {caseData.vitals.o2_saturation.value}</div>
                                )}
                                {caseData.vitals?.temperature && (
                                    <div><strong>Temp:</strong> {caseData.vitals.temperature.value}</div>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 'metadata':
                return (
                    <div className="tab-content metadata-tab">
                        <div className="form-group">
                            <label>Case Name</label>
                            <input
                                type="text"
                                value={caseData.metadata?.case_name || ''}
                                onChange={(e) => updateField('metadata.case_name', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Domain</label>
                            <input
                                type="text"
                                value={caseData.metadata?.domain || ''}
                                onChange={(e) => updateField('metadata.domain', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Core Threat</label>
                            <input
                                type="text"
                                value={caseData.metadata?.core_threat || ''}
                                onChange={(e) => updateField('metadata.core_threat', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Cognitive Trap</label>
                            <input
                                type="text"
                                value={caseData.metadata?.cognitive_trap || ''}
                                onChange={(e) => updateField('metadata.cognitive_trap', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Red Flag Density</label>
                            <select
                                value={caseData.metadata?.red_flag_density || 'Low'}
                                onChange={(e) => updateField('metadata.red_flag_density', e.target.value)}
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Intended Difficulty</label>
                            <select
                                value={caseData.metadata?.intended_difficulty || 'Basic'}
                                onChange={(e) => updateField('metadata.intended_difficulty', e.target.value)}
                            >
                                <option value="Basic">Basic</option>
                                <option value="Intermediate">Intermediate</option>
                                <option value="Advanced">Advanced</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Failure Mode</label>
                            <input
                                type="text"
                                value={caseData.metadata?.failure_mode || ''}
                                onChange={(e) => updateField('metadata.failure_mode', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>TTS Voice</label>
                            <input
                                type="text"
                                value={caseData.tts_voice || 'Puck'}
                                onChange={(e) => updateField('tts_voice', e.target.value)}
                            />
                        </div>
                    </div>
                );

            case 'persona':
                return (
                    <div className="tab-content persona-tab">
                        <h4>Demographics</h4>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Age</label>
                                <input
                                    type="number"
                                    value={caseData.persona?.demographics?.age || ''}
                                    onChange={(e) => updateField('persona.demographics.age', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Sex</label>
                                <select
                                    value={caseData.persona?.demographics?.sex || 'Male'}
                                    onChange={(e) => updateField('persona.demographics.sex', e.target.value)}
                                >
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <h4>Psychometrics</h4>
                        <div className="form-group">
                            <label>Affect</label>
                            <input
                                type="text"
                                value={caseData.persona?.psychometrics?.affect || ''}
                                onChange={(e) => updateField('persona.psychometrics.affect', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Opening Stance</label>
                            <input
                                type="text"
                                value={caseData.persona?.psychometrics?.opening_stance || ''}
                                onChange={(e) => updateField('persona.psychometrics.opening_stance', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Reliability</label>
                            <select
                                value={caseData.persona?.psychometrics?.reliability || 'High'}
                                onChange={(e) => updateField('persona.psychometrics.reliability', e.target.value)}
                            >
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Volunteering Style</label>
                            <input
                                type="text"
                                value={caseData.persona?.psychometrics?.volunteering_style || ''}
                                onChange={(e) => updateField('persona.psychometrics.volunteering_style', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Medical Literacy</label>
                            <select
                                value={caseData.persona?.psychometrics?.medical_literacy || 'Moderate'}
                                onChange={(e) => updateField('persona.psychometrics.medical_literacy', e.target.value)}
                            >
                                <option value="Low">Low</option>
                                <option value="Moderate">Moderate</option>
                                <option value="High">High</option>
                            </select>
                        </div>
                    </div>
                );

            case 'clinical':
                return (
                    <div className="tab-content clinical-tab">
                        <div className="array-header">
                            <h4>Clinical Data Items</h4>
                            <button
                                className="add-btn"
                                onClick={() => addArrayItem('clinical_data', {
                                    category: 'HPI',
                                    label: 'New Item',
                                    value: ''
                                })}
                            >
                                + Add Item
                            </button>
                        </div>
                        <div className="clinical-items">
                            {(caseData.clinical_data || []).map((item, index) => (
                                <div key={item.id || index} className="clinical-item">
                                    <div className="item-header">
                                        <span className="item-id">{item.id}</span>
                                        <button
                                            className="remove-btn"
                                            onClick={() => removeArrayItem('clinical_data', index)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Category</label>
                                            <select
                                                value={item.category || 'HPI'}
                                                onChange={(e) => updateArrayItem('clinical_data', index, 'category', e.target.value)}
                                            >
                                                <option value="Chief Complaint">Chief Complaint</option>
                                                <option value="HPI">HPI</option>
                                                <option value="PMH">PMH</option>
                                                <option value="Meds">Meds</option>
                                                <option value="Allergies">Allergies</option>
                                                <option value="Social">Social</option>
                                                <option value="Family History">Family History</option>
                                                <option value="Review of Systems">Review of Systems</option>
                                                <option value="Physical Exam">Physical Exam</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Label</label>
                                            <input
                                                type="text"
                                                value={item.label || ''}
                                                onChange={(e) => updateArrayItem('clinical_data', index, 'label', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Value</label>
                                        <textarea
                                            value={item.value || ''}
                                            onChange={(e) => updateArrayItem('clinical_data', index, 'value', e.target.value)}
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'vitals':
                return (
                    <div className="tab-content vitals-tab">
                        <h4>Vital Signs</h4>
                        {['heart_rate', 'blood_pressure', 'respiratory_rate', 'o2_saturation', 'temperature'].map(vital => (
                            <div key={vital} className="vital-item">
                                <div className="form-group">
                                    <label>{vital.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                                    <input
                                        type="text"
                                        value={caseData.vitals?.[vital]?.value || ''}
                                        onChange={(e) => updateField(`vitals.${vital}.value`, e.target.value)}
                                        placeholder="Value"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <input
                                        type="text"
                                        value={caseData.vitals?.[vital]?.status || caseData.vitals?.[vital]?.context || ''}
                                        onChange={(e) => updateField(`vitals.${vital}.status`, e.target.value)}
                                        placeholder="Status/Context"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'rubric':
                return (
                    <div className="tab-content rubric-tab">
                        {/* Critical Failures */}
                        <div className="rubric-section">
                            <div className="array-header">
                                <h4 className="cf">Critical Failures (CF)</h4>
                                <button
                                    className="add-btn"
                                    onClick={() => addArrayItem('scoring_rubric.critical_failures', {
                                        description: 'New critical failure criterion'
                                    })}
                                >
                                    + Add
                                </button>
                            </div>
                            {(caseData.scoring_rubric?.critical_failures || []).map((item, index) => (
                                <div key={`cf-${index}`} className="rubric-item cf">
                                    <input
                                        type="text"
                                        className="rubric-id"
                                        value={item.id || ''}
                                        onChange={(e) => updateArrayItem('scoring_rubric.critical_failures', index, 'id', e.target.value)}
                                        placeholder="CF-X"
                                    />
                                    <input
                                        type="text"
                                        className="rubric-desc"
                                        value={item.description || ''}
                                        onChange={(e) => updateArrayItem('scoring_rubric.critical_failures', index, 'description', e.target.value)}
                                        placeholder="Description"
                                    />
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeArrayItem('scoring_rubric.critical_failures', index)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Quality Failures */}
                        <div className="rubric-section">
                            <div className="array-header">
                                <h4 className="qf">Quality Failures (QF)</h4>
                                <button
                                    className="add-btn"
                                    onClick={() => addArrayItem('scoring_rubric.quality_failures', {
                                        description: 'New quality failure criterion'
                                    })}
                                >
                                    + Add
                                </button>
                            </div>
                            {(caseData.scoring_rubric?.quality_failures || []).map((item, index) => (
                                <div key={`qf-${index}`} className="rubric-item qf">
                                    <input
                                        type="text"
                                        className="rubric-id"
                                        value={item.id || ''}
                                        onChange={(e) => updateArrayItem('scoring_rubric.quality_failures', index, 'id', e.target.value)}
                                        placeholder="QF-X"
                                    />
                                    <input
                                        type="text"
                                        className="rubric-desc"
                                        value={item.description || ''}
                                        onChange={(e) => updateArrayItem('scoring_rubric.quality_failures', index, 'description', e.target.value)}
                                        placeholder="Description"
                                    />
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeArrayItem('scoring_rubric.quality_failures', index)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Excellence Signals */}
                        <div className="rubric-section">
                            <div className="array-header">
                                <h4 className="ex">Excellence Signals (EX)</h4>
                                <button
                                    className="add-btn"
                                    onClick={() => addArrayItem('scoring_rubric.excellence_signals', {
                                        description: 'New excellence signal'
                                    })}
                                >
                                    + Add
                                </button>
                            </div>
                            {(caseData.scoring_rubric?.excellence_signals || []).map((item, index) => (
                                <div key={`ex-${index}`} className="rubric-item ex">
                                    <input
                                        type="text"
                                        className="rubric-id"
                                        value={item.id || ''}
                                        onChange={(e) => updateArrayItem('scoring_rubric.excellence_signals', index, 'id', e.target.value)}
                                        placeholder="EX-X"
                                    />
                                    <input
                                        type="text"
                                        className="rubric-desc"
                                        value={item.description || ''}
                                        onChange={(e) => updateArrayItem('scoring_rubric.excellence_signals', index, 'description', e.target.value)}
                                        placeholder="Description"
                                    />
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeArrayItem('scoring_rubric.excellence_signals', index)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="case-editor-overlay" onClick={onClose}>
            <div className="case-editor-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="editor-header">
                    <h2>{caseId}</h2>
                    <div className="header-actions">
                        {isConfigured && !isAuthenticated && (
                            <button className="sign-in-btn" onClick={signIn}>
                                Sign in to Edit
                            </button>
                        )}
                        {isAuthenticated && (
                            <span className="user-info">
                                {user?.picture && <img src={user.picture} alt="" className="user-avatar" />}
                                {user?.name || user?.email}
                            </span>
                        )}
                        <button className="close-btn" onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* Error display */}
                {error && (
                    <div className="editor-error">
                        {error}
                    </div>
                )}

                {/* Loading state */}
                {isLoading && (
                    <div className="editor-loading">
                        Loading case data...
                    </div>
                )}

                {/* Content */}
                {!isLoading && caseData && (
                    <>
                        {/* Tabs */}
                        <div className="editor-tabs">
                            {TABS.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab content */}
                        <div className="editor-content">
                            {renderTabContent()}
                        </div>

                        {/* Footer */}
                        <div className="editor-footer">
                            {hasChanges && (
                                <span className="unsaved-indicator">● Unsaved changes</span>
                            )}
                            <div className="footer-actions">
                                <button className="cancel-btn" onClick={onClose}>
                                    Cancel
                                </button>
                                <button
                                    className="save-btn"
                                    onClick={handleSave}
                                    disabled={!isAuthenticated || isSaving || !hasChanges}
                                >
                                    {isSaving ? 'Saving...' : 'Save to Cloud'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CaseEditor;
